// deps
import React, { useContext, useCallback } from "react";

// internal deps
import { IUpiPaymentGroup, UpiGroup } from "@swiggy-private/payment-ui";
import { AnalyticsContext } from "@swiggy-private/checkout-core";

// interfaces
import { PaymentGroupHandlerProps } from "@payment-client/components/show-payments/components/payment-group-handler/interfaces";

// hooks
import { useUPIHandler } from "@payment-client/components/show-payments/components/payment-group-handler/hooks/UseUPIHandler";

// events
import {
    clickViewAllBtn,
    impressionViewAll,
    clickPaymentGroups,
    clickAddNew,
    impressionAddNew,
    impressionPaymentGroups,
    impressionPaymentGroupsExpanded,
    clickInAppOnBoarding,
} from "@payment-client/analytics/events/SmartUpi";

interface UPIGroupHandlerProps extends Omit<PaymentGroupHandlerProps, "paymentGroup"> {
    paymentGroup: IUpiPaymentGroup["paymentData"];
}

export function UPIGroupHandler(props: UPIGroupHandlerProps): React.ReactElement | null {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    const { onSelect, onPay, paymentGroup, ...otherProps } = props;
    const { paymentActions, availableUpiIntentMethods, upiIntentMethodFetchInProgress } = useUPIHandler(props);

    const onClickViewAllBtn = useCallback(
        (context: string) => {
            clickViewAllBtn(context, clickEvent);
        },
        [clickEvent],
    );

    const onImpressionViewAll = useCallback(() => {
        impressionViewAll(impressionEvent);
    }, [impressionEvent]);

    const onClickAddNew = useCallback(
        (value: string) => {
            clickAddNew(value, clickEvent);
        },
        [clickEvent],
    );

    const onImpressionAddNew = useCallback(() => {
        impressionAddNew(impressionEvent);
    }, [impressionEvent]);

    const onClickPaymentGroups = useCallback(
        (context: string, label: string, value: string) => {
            clickPaymentGroups(context, label, value, clickEvent);
        },
        [clickEvent],
    );

    const onClickInAppOnBoarding = useCallback(() => {
        clickInAppOnBoarding(clickEvent);
    }, [clickEvent]);

    const onImpressionPaymentGroups = useCallback(
        (methodList: string) => {
            impressionPaymentGroups(methodList, impressionEvent);
        },
        [impressionEvent],
    );

    const onImpressionPaymentGroupsExpanded = useCallback(
        (methodList: string) => {
            impressionPaymentGroupsExpanded(methodList, impressionEvent);
        },
        [impressionEvent],
    );

    return (
        <UpiGroup
            paymentActions={paymentActions}
            paymentData={props.paymentGroup}
            availableUpiIntentMethods={availableUpiIntentMethods}
            upiIntentMethodFetchInProgress={upiIntentMethodFetchInProgress}
            onClickViewAllBtn={onClickViewAllBtn}
            onImpressionViewAll={onImpressionViewAll}
            onClickAddNew={onClickAddNew}
            onImpressionAddNew={onImpressionAddNew}
            onClickPaymentGroups={onClickPaymentGroups}
            onClickInAppOnBoarding={onClickInAppOnBoarding}
            onImpressionPaymentGroups={onImpressionPaymentGroups}
            onImpressionPaymentGroupsExpanded={onImpressionPaymentGroupsExpanded}
            {...otherProps}
        />
    );
}
